'use client';
import React, { useEffect, useCallback } from 'react';
import styled from '@benzinga/themetron';

import Tabs from '../lib/components/Tabs';
import { convertAgGridColumnDefsToTableColumnDefs, injectLogos } from '../lib/utils';
import calendars from './CalendarsDefs/Calendars';
import { IFetchParams } from './CalendarsDefs/Calendars';
import { Icon } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';

import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TableVirtualized } from '@benzinga/table';
import { NoRowsOverlay } from '../NoRowsOverlay';
import { usePermission } from '@benzinga/user-context';
import type { CalendarEvents } from '@benzinga/calendar-manager';
import type { OptionsActivity } from '@benzinga/calendar-option-activity-manager';

const BzEdgeCTA = React.lazy(() => import('@benzinga/ads').then(module => ({ default: module.BzEdgeCTA })));

export type ICalendarKeys = 'earnings' | 'ratings' | 'option_activity' | 'dividends' | 'ipos';
// | 'economics'
// | 'splits'
// | 'conference'
// | 'ma'

export interface ICalendarsWidgetProps {
  params?: IFetchParams;
  initialTab?: ICalendarKeys;
  initialTickers?: string[];
  injectTickersLogos?: boolean;
  title?: string;
  visibleTitle?: boolean;
  hideTabs?: boolean;
  enablePaywall?: boolean;
}

const STYLE_MAIN_COLOR = '#2ca2d1';

export const CalendarsWidget: React.FC<ICalendarsWidgetProps> = ({
  enablePaywall = true,
  hideTabs = false,
  initialTab,
  initialTickers,
  injectTickersLogos = false,
  params: additionalParams,
  title = 'Calendar',
  visibleTitle = true,
}) => {
  const getTabIndexByKey = useCallback((key: string) => {
    return calendars.findIndex(el => el.key === key);
  }, []);

  let initialTabIndex = 0;
  if (initialTab) {
    initialTabIndex = getTabIndexByKey(initialTab);
  }

  const session = React.useContext(SessionContext);
  const [activeTabIndex, setActiveTabIndex] = React.useState(initialTabIndex);
  const [loading, setLoading] = React.useState(true);
  const [tableData, setTableData] = React.useState<(CalendarEvents | OptionsActivity)[]>([]);
  const hasPermission = usePermission('com/read', 'unlimited-calendars');

  const fetch = useCallback(() => {
    setLoading(true);
    let params: IFetchParams = {};
    if (initialTickers) {
      params['symbols'] = initialTickers;
    }
    params = { ...params, ...additionalParams };
    const key = calendars[activeTabIndex].key;
    calendars[activeTabIndex]
      .fetch(session.getSession(), params)
      .then(({ data }) => {
        const items = data[key];
        if (injectTickersLogos && Array.isArray(items) && items.length && items[0].ticker) {
          return injectLogos(items as Array<CalendarEvents | OptionsActivity>);
        }
        return items;
      })
      .then(items => {
        setTableData(items);
        setLoading(false);
      });
  }, [session, activeTabIndex, additionalParams, initialTickers, injectTickersLogos]);

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex]);

  const tabs = useCallback(() => {
    return calendars.map(el => {
      return {
        key: el.key,
        link: el.link,
        title: el.tab,
      };
    });
  }, []);

  const columnDefs = useCallback(() => {
    return calendars[activeTabIndex].columns;
  }, [activeTabIndex]);

  const getCalendarTitle = useCallback((): string => {
    return calendars[activeTabIndex].tab;
  }, [activeTabIndex]);

  const getSeeMoreLink = useCallback((): string => {
    return calendars[activeTabIndex].link;
  }, [activeTabIndex]);

  const onTabChange = useCallback(
    (index: number): void => {
      if (activeTabIndex === index) return;
      setTableData([]);
      setActiveTabIndex(index);
    },
    [activeTabIndex],
  );

  const SeeMoreLink = ({ calendarTitle, link }: { calendarTitle: string; link: string }): React.ReactElement => {
    if (!calendarTitle || !link) return <></>;

    return (
      <a data-action="Calendars Widget See More Click" href={link} rel="noreferrer" target="_blank">
        Click to see more {calendarTitle} updates
      </a>
    );
  };

  const formattedColumnsDef = convertAgGridColumnDefsToTableColumnDefs(columnDefs());

  return (
    <CalendarWidget id="calendar-widget">
      <CalendarsWrapper className="calendar-wrapper">
        {visibleTitle && <CalendarTitle>{title}</CalendarTitle>}
        {!hideTabs && (
          <CalendarWidgetHeader>
            <Tabs initialTabIndex={activeTabIndex} onChange={onTabChange} tabs={tabs()} />
          </CalendarWidgetHeader>
        )}
        <TableWrapper>
          {/* <Table
            calendarKey={calendars[activeTabIndex].key}
            calendarName={calendars[activeTabIndex].tab}
            columnDefs={columnDefs()}
            data={tableData}
            initialTickers={initialTickers}
            loading={loading}
          /> */}
          <TableVirtualized
            columnsDef={formattedColumnsDef}
            customNoDataElement={
              <NoRowsOverlay calendarName={calendars[activeTabIndex].tab} initialTickers={initialTickers} />
            }
            height={400}
            isGated={enablePaywall ? !hasPermission : false}
            isLoading={loading}
            rowData={tableData}
          />
          <SeeMoreLinkWrapper>
            <SeeMoreLink calendarTitle={getCalendarTitle()} link={getSeeMoreLink()} />
            <ChevronRight>
              <Icon icon={faChevronRight} />
            </ChevronRight>
          </SeeMoreLinkWrapper>
          {enablePaywall && !hasPermission && (
            <div className="absolute top-1/3 w-full">
              <BzEdgeCTA type={calendars[activeTabIndex].edgeAdKey} />
            </div>
          )}
        </TableWrapper>
      </CalendarsWrapper>
    </CalendarWidget>
  );
};

const CalendarWidget = styled.div`
  font-family:
    Helvetica Neue,
    Helvetica,
    OpenSans,
    Arial,
    sans-serif;
`;

const CalendarsWrapper = styled.div`
  position: relative;

  a {
    line-height: inherit;
    color: #2ca2d1;
    text-decoration: none;
    cursor: pointer;
  }
  img {
    display: inline-block;
    vertical-align: middle;
    max-width: 100%;
    height: auto;
  }
`;

const CalendarWidgetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow-x: auto;
  margin-bottom: 0.5rem;

  @media only screen and (max-width: 800px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CalendarTitle = styled.h5`
  margin-bottom: 8px;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 24px;
  color: #283d59;
`;

const TableWrapper = styled.div`
  /* border: 1px solid #cbe0f2; */
  box-sizing: border-box;
  /* border-radius: 4px; */
  background: rgba(26, 121, 255, 0.025);
  overflow: hidden;
  position: relative;
`;

const ChevronRight = styled.div`
  display: block;
  background: #f2f8ff;
  border: 0.5px solid #e1ebfa;
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  border-radius: 50px;
  font-size: 9px;
  color: ${STYLE_MAIN_COLOR};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SeeMoreLinkWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-top: none;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  a {
    font-size: 14px;
    line-height: 20px;
    text-decoration: none;
    font-size: 0.875rem;
    cursor: pointer;
    font-weight: 500;
    color: ${STYLE_MAIN_COLOR};
    margin-right: 8px;

    &:hover {
      text-decoration: underline;
    }
  }
`;
